import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Sparkles, TrendingUp, Users, CreditCard, Target as TargetIcon,
  Megaphone, Compass, LineChart, CheckCircle2, XCircle, RefreshCw,
  AlertCircle, Loader2, Copy, Check, PlayCircle, Link2, Send, Unlink,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";
import { apiErrorToast } from "../lib/utils";

const AGENTS = [
  { key: "conversion", label: "Conversion Optimizer", icon: LineChart,
    blurb: "Analyze real funnel data and recommend the highest-impact fixes." },
  { key: "marketing", label: "Growth Marketing", icon: Megaphone,
    blurb: "Draft subscription-focused copy for ads, posts, and reels." },
  { key: "leads", label: "High-Intent Leads", icon: Compass,
    blurb: "Find public communities and craft personalized outreach drafts." },
];

function KpiCard({ label, value, sub, icon: Icon, testid, accent = "indigo" }) {
  const ring = {
    indigo: "bg-indigo-50 text-indigo-700 ring-indigo-200",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-200",
    rose: "bg-rose-50 text-rose-700 ring-rose-200",
    zinc: "bg-zinc-50 text-zinc-700 ring-zinc-200",
  }[accent];
  return (
    <div data-testid={testid} className="rounded-xl bg-white border border-[#E8DDC9] p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
          <div className="mt-2 text-2xl font-display font-black text-zinc-900">{value}</div>
          {sub ? <div className="mt-1 text-xs text-zinc-500">{sub}</div> : null}
        </div>
        <div className={`h-9 w-9 rounded-lg ring-1 flex items-center justify-center ${ring}`}>
          <Icon size={16} />
        </div>
      </div>
    </div>
  );
}

function Bar({ label, value, max, muted }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex-1 flex items-center gap-3">
      <div className="w-40 text-xs text-zinc-700 shrink-0">{label}</div>
      <div className="flex-1 h-5 bg-zinc-100 rounded-md overflow-hidden">
        <div
          className={`h-full ${muted ? "bg-zinc-300" : "bg-gradient-to-r from-indigo-500 to-emerald-500"} transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="w-16 text-right text-xs font-mono font-semibold">{value.toLocaleString()}</div>
    </div>
  );
}

function CopyBtn({ text, testid }) {
  const [done, setDone] = useState(false);
  return (
    <button
      data-testid={testid}
      onClick={async () => {
        try { await navigator.clipboard.writeText(text || ""); setDone(true); setTimeout(() => setDone(false), 1500); }
        catch { toast.error("Copy failed"); }
      }}
      className="inline-flex items-center gap-1 text-[11px] text-zinc-600 hover:text-zinc-900"
    >
      {done ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />} {done ? "Copied" : "Copy"}
    </button>
  );
}

function ActionCard({ a, onDecision, onPostToReddit, redditReady }) {
  const [note, setNote] = useState("");
  const stateColor = {
    pending: "border-amber-200 bg-amber-50",
    approved: "border-emerald-200 bg-emerald-50",
    rejected: "border-rose-200 bg-rose-50",
    applied: "border-sky-200 bg-sky-50",
  }[a.status] || "border-zinc-200 bg-white";
  const isRedditPost = a.action_type === "reddit_post";
  return (
    <div className={`rounded-xl border p-4 ${stateColor}`} data-testid={`action-card-${a.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display font-bold text-sm text-zinc-900 truncate">{a.title}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
            {a.agent} {a.stage ? `· ${a.stage}` : ""} {a.channel ? `· ${a.channel}` : ""} · {a.status}
          </div>
        </div>
        {a.status === "pending" && (
          <div className="flex gap-1 shrink-0">
            <button data-testid={`action-approve-${a.id}`} onClick={() => onDecision(a.id, "approve", note)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-600 text-white text-[11px] font-semibold hover:bg-emerald-700">
              <CheckCircle2 size={12} /> Approve
            </button>
            <button data-testid={`action-reject-${a.id}`} onClick={() => onDecision(a.id, "reject", note)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-rose-300 text-rose-700 text-[11px] font-semibold hover:bg-rose-100">
              <XCircle size={12} /> Reject
            </button>
          </div>
        )}
        {a.status === "approved" && (
          <div className="flex gap-1 shrink-0">
            {isRedditPost && (
              <button data-testid={`action-post-reddit-${a.id}`}
                onClick={() => onPostToReddit(a)}
                disabled={!redditReady}
                title={redditReady ? "" : "Connect your Reddit account first (Leads tab)"}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#FF4500] text-white text-[11px] font-semibold hover:bg-[#e23e00] disabled:opacity-50 disabled:cursor-not-allowed">
                <Send size={12} /> Post as me to Reddit
              </button>
            )}
            <button data-testid={`action-applied-${a.id}`} onClick={() => onDecision(a.id, "applied", note)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-sky-600 text-white text-[11px] font-semibold hover:bg-sky-700">
              <PlayCircle size={12} /> Mark applied
            </button>
          </div>
        )}
        {a.status === "applied" && a.external_url && (
          <a href={a.external_url} target="_blank" rel="noreferrer" data-testid={`action-external-${a.id}`}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-sky-300 text-sky-700 text-[11px] font-semibold hover:bg-sky-50 shrink-0">
            View on {a.external_platform || "source"} →
          </a>
        )}
      </div>

      {/* Agent-specific body */}
      {a.agent === "conversion" && (
        <div className="mt-3 space-y-2 text-sm text-zinc-800">
          {a.hypothesis && <p><span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Hypothesis</span><br/>{a.hypothesis}</p>}
          {a.recommended_change && <p><span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Recommended change</span><br/>{a.recommended_change}</p>}
          <div className="flex flex-wrap gap-3 text-[11px] text-zinc-600 pt-1">
            {a.effort && <span>Effort: <b>{a.effort}</b></span>}
            {a.expected_lift_pct != null && <span>Expected lift: <b>{a.expected_lift_pct}%</b></span>}
            {a.risk && <span>Risk: {a.risk}</span>}
          </div>
          {a.evidence && <div className="text-[11px] text-zinc-500 italic">Evidence: {a.evidence}</div>}
        </div>
      )}
      {a.agent === "marketing" && (
        <div className="mt-3 space-y-2 text-sm text-zinc-800">
          {a.body && (
            <div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Draft</span>
                <CopyBtn text={a.body} testid={`copy-${a.id}`} />
              </div>
              <p className="whitespace-pre-wrap leading-relaxed">{a.body}</p>
            </div>
          )}
          {a.cta && <div className="text-[12px] text-zinc-700">CTA: <b>{a.cta}</b></div>}
          {a.why_it_works && <div className="text-[11px] text-zinc-500 italic">Why it works: {a.why_it_works}</div>}
          <div className="text-[11px] text-zinc-500">Audience: {a.audience} · Est. cost: {a.estimated_cost}</div>
        </div>
      )}
      {a.agent === "leads" && (
        <div className="mt-3 space-y-2 text-sm text-zinc-800">
          {a.action_type === "reddit_post" ? (
            <>
              <div className="flex flex-wrap items-center gap-2 text-[11px]">
                <span className="inline-flex items-center gap-1 bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded-full">
                  Reddit · r/{a.subreddit}
                </span>
                {a.intent_score ? (
                  <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">
                    Intent {a.intent_score}/10
                  </span>
                ) : null}
                <span className="text-zinc-500">· {a.post_upvotes} upvotes · {a.post_comments} comments · u/{a.author}</span>
                {a.is_safe_to_post === false && (
                  <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
                    <AlertCircle size={10}/> self-promo risk
                  </span>
                )}
              </div>
              {a.post_excerpt && (
                <div className="rounded border border-zinc-200 bg-white/60 p-2 text-[12px] text-zinc-700 whitespace-pre-wrap">
                  <div className="font-semibold text-zinc-800 mb-1">{a.post_title}</div>
                  {a.post_excerpt}
                </div>
              )}
              {a.url && (
                <a href={a.url} target="_blank" rel="noreferrer" className="text-xs text-indigo-700 hover:underline break-all">
                  Open on Reddit →
                </a>
              )}
              {a.intent_reason && (
                <div className="text-[11px] text-zinc-500 italic">Why high intent: {a.intent_reason}</div>
              )}
              {a.outreach_draft && (
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Draft reply (post manually)</span>
                    <CopyBtn text={a.outreach_draft} testid={`copy-${a.id}`} />
                  </div>
                  <p className="whitespace-pre-wrap leading-relaxed">{a.outreach_draft}</p>
                </div>
              )}
              {a.compliance_notes && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-1.5">
                  <AlertCircle size={12} className="shrink-0 mt-0.5"/> {a.compliance_notes}
                </div>
              )}
            </>
          ) : (
            <>
              {a.url && a.url !== "TBD" && (
                <a href={a.url} target="_blank" rel="noreferrer" className="text-xs text-indigo-700 hover:underline break-all">{a.url}</a>
              )}
              {a.why_fit && <div className="text-[12px]"><span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Why fit · </span>{a.why_fit}</div>}
              {a.outreach_draft && (
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Outreach draft ({a.outreach_channel || "channel TBD"})</span>
                    <CopyBtn text={a.outreach_draft} testid={`copy-${a.id}`} />
                  </div>
                  <p className="whitespace-pre-wrap leading-relaxed">{a.outreach_draft}</p>
                </div>
              )}
              {a.compliance_notes && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-1.5">
                  <AlertCircle size={12} className="shrink-0 mt-0.5"/> {a.compliance_notes}
                </div>
              )}
              <div className="text-[11px] text-zinc-500 italic">Not verified. Review the community rules before you post.</div>
            </>
          )}
        </div>
      )}

      {a.status === "pending" && (
        <input
          data-testid={`action-note-${a.id}`}
          type="text" value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Optional decision note (e.g. 'ship Monday', 'spend_inr=1200')"
          className="mt-3 w-full text-xs h-8 px-2 border border-zinc-300 rounded-md bg-white"
        />
      )}
      {a.decision_note && (
        <div className="mt-2 text-[11px] text-zinc-500">Note: {a.decision_note}</div>
      )}
    </div>
  );
}

export default function GrowthDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("conversion");
  const [leadsMode, setLeadsMode] = useState("reddit");   // "reddit" | "communities"
  const [metrics, setMetrics] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [actions, setActions] = useState([]);
  const [integrations, setIntegrations] = useState({});
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  // Marketing / Leads form state
  const [mkt, setMkt] = useState({ audience: "early-career software engineers in India preparing for FAANG interviews", channel: "linkedin", goal: "paid_subscription", count: 4 });
  const [leads, setLeads] = useState({ niche: "Indian tier-2 engineering college placement cells", count: 5, outreach_tone: "helpful" });
  const [reddit, setReddit] = useState({
    query: "resume rejected OR interview failed OR skill gap",
    subreddits: "cscareerquestions, developersIndia, resumes",
    limit: 8, time_filter: "month", tone: "helpful",
  });

  const refresh = async () => {
    try {
      setLoading(true);
      const [m, f, a, intg] = await Promise.all([
        api.get("/admin/growth/metrics"),
        api.get("/admin/growth/funnel"),
        api.get("/admin/growth/actions", { params: { limit: 100 } }),
        api.get("/admin/growth/integrations/status").catch(() => ({ data: {} })),
      ]);
      setMetrics(m.data); setFunnel(f.data); setActions(a.data.items || []);
      setIntegrations(intg.data || {});
    } catch (e) { apiErrorToast(e, "Failed to load growth data"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (user && !user.is_admin) return;  // non-admin: don't call admin endpoints, Navigate will redirect
    refresh();
    // Handle Reddit OAuth callback (?reddit=connected|error&detail=...)
    try {
      const q = new URLSearchParams(window.location.search);
      const r = q.get("reddit");
      if (r === "connected") {
        toast.success(`Reddit connected as u/${q.get("detail") || "ok"}`);
      } else if (r === "error") {
        toast.error(`Reddit connect failed: ${q.get("detail") || "unknown"}`);
      }
      if (r) {
        // Strip the query params so refresh is clean.
        const url = new URL(window.location.href);
        url.searchParams.delete("reddit");
        url.searchParams.delete("detail");
        window.history.replaceState({}, "", url.toString());
      }
    } catch { /* no-op */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const connectReddit = async () => {
    try {
      const r = await api.get("/admin/growth/reddit/auth-url");
      // Open Reddit's consent screen in the SAME tab so the redirect lands back here.
      window.location.href = r.data.authorize_url;
    } catch (e) { apiErrorToast(e, "Could not start Reddit connect"); }
  };
  const disconnectReddit = async () => {
    if (!window.confirm("Disconnect your Reddit account? Drafts stay, but no posting until you reconnect.")) return;
    try {
      await api.post("/admin/growth/reddit/disconnect");
      toast.success("Reddit disconnected");
      refresh();
    } catch (e) { apiErrorToast(e, "Reddit disconnect failed"); }
  };
  const postToReddit = async (action) => {
    const draft = action.outreach_draft || "";
    const text = window.prompt(
      `Post this reply to r/${action.subreddit} as u/${integrations?.reddit?.reddit_username || "you"}?\n\nEdit if needed:`,
      draft,
    );
    if (text == null) return;
    if ((text || "").trim().length < 5) { toast.error("Reply is too short"); return; }
    try {
      setRunning(true);
      const r = await api.post(`/admin/growth/actions/${action.id}/post-to-reddit`, { text });
      toast.success(`Posted! ${r.data.permalink || "comment created"}`);
      refresh();
    } catch (e) { apiErrorToast(e, "Reddit post failed"); }
    finally { setRunning(false); }
  };

  const runConversion = async () => {
    try {
      setRunning(true);
      const r = await api.post("/admin/growth/conversion/analyze");
      toast.success(`Generated ${r.data.actions.length} recommendation(s)`);
      if (r.data.data_quality_note) toast.info(r.data.data_quality_note);
      refresh();
    } catch (e) { apiErrorToast(e, "Conversion agent failed"); }
    finally { setRunning(false); }
  };
  const runMarketing = async () => {
    try {
      setRunning(true);
      const r = await api.post("/admin/growth/marketing/generate", mkt);
      toast.success(`Generated ${r.data.variants.length} draft(s)`);
      refresh();
    } catch (e) { apiErrorToast(e, "Marketing agent failed"); }
    finally { setRunning(false); }
  };
  const runLeads = async () => {
    try {
      setRunning(true);
      const r = await api.post("/admin/growth/leads/generate", leads);
      toast.success(`Generated ${r.data.prospects.length} prospect(s)`);
      refresh();
    } catch (e) { apiErrorToast(e, "Lead-gen agent failed"); }
    finally { setRunning(false); }
  };
  const runReddit = async () => {
    try {
      setRunning(true);
      const payload = {
        query: reddit.query,
        limit: Number(reddit.limit) || 8,
        time_filter: reddit.time_filter,
        tone: reddit.tone,
        subreddits: (reddit.subreddits || "")
          .split(",").map(s => s.trim().replace(/^r\//i, "")).filter(Boolean),
      };
      const r = await api.post("/admin/growth/leads/reddit", payload);
      if (!r.data.prospects.length) {
        toast.info("No matching public Reddit posts in that window.");
      } else {
        toast.success(`Found ${r.data.prospects.length} real Reddit post(s)`);
      }
      refresh();
    } catch (e) { apiErrorToast(e, "Reddit lead agent failed"); }
    finally { setRunning(false); }
  };
  const toggleLeadsMode = (mode) => setLeadsMode(mode);

  const decide = async (id, decision, note) => {
    try {
      await api.post(`/admin/growth/actions/${id}/${decision}`, { note: note || null });
      const label = decision === "approve" ? "approved" : decision === "reject" ? "rejected" : "applied";
      toast.success(`Marked ${label}`);
      refresh();
    } catch (e) { apiErrorToast(e, "Failed to update action"); }
  };

  const agentActions = useMemo(
    () => actions.filter(a => a.agent === tab),
    [actions, tab]
  );

  const maxStage = useMemo(
    () => Math.max(1, ...(funnel?.stages || []).map(s => s.value)),
    [funnel]
  );

  if (!user) return null;
  if (!user.is_admin) return <Navigate to="/app/dashboard" replace />;

  return (
    <div className="p-6 md:p-10" data-testid="growth-dashboard">
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Admin · Growth Suite</div>
          <h1 className="font-display font-black text-3xl tracking-tight mt-1">Subscription Growth Agents</h1>
          <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
            Three AI assistants that help you ship more paid subscriptions — not vanity clicks.
            Nothing publishes externally; every recommendation waits for your approval.
          </p>
        </div>
        <button onClick={refresh} data-testid="growth-refresh"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-zinc-300 bg-white text-sm font-semibold hover:bg-zinc-50">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* KPI strip */}
      {loading && !metrics ? (
        <div className="h-24 flex items-center justify-center text-zinc-500 text-sm"><Loader2 className="animate-spin mr-2" size={16}/>Loading metrics…</div>
      ) : metrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
          <KpiCard label="Visitors (30d)" value={(metrics.events.unique_sessions || 0).toLocaleString()}
            sub={metrics.events.data_available ? "events logged" : "install tracking"} icon={Users} testid="kpi-visitors"
            accent={metrics.events.data_available ? "indigo" : "zinc"} />
          <KpiCard label="Signups (30d)" value={metrics.subscription.signups.toLocaleString()}
            sub={metrics.rates.visitor_to_signup_pct != null ? `${metrics.rates.visitor_to_signup_pct}% of visitors` : "visitor data N/A"}
            icon={TrendingUp} testid="kpi-signups" accent="emerald"/>
          <KpiCard label="Checkout starts" value={metrics.subscription.checkout_starts.toLocaleString()}
            sub="Razorpay orders" icon={CreditCard} testid="kpi-checkouts" />
          <KpiCard label="Paid subscribers" value={metrics.subscription.paid_subs.toLocaleString()}
            sub={metrics.rates.signup_to_paid_pct != null ? `${metrics.rates.signup_to_paid_pct}% of signups` : "—"}
            icon={Sparkles} testid="kpi-paid" accent="emerald" />
          <KpiCard label="Revenue (30d)" value={`₹${metrics.subscription.revenue_inr.toLocaleString()}`}
            sub={`Active: ${metrics.subscription.active_subs}`} icon={CreditCard} testid="kpi-revenue" accent="amber" />
          <KpiCard label="Cost / paid sub"
            value={metrics.spend.cost_per_paid_subscriber_inr != null ? `₹${metrics.spend.cost_per_paid_subscriber_inr}` : "N/A"}
            sub={metrics.spend.data_available ? `Spend ₹${metrics.spend.logged_spend_inr}` : "log spend to see CPA"}
            icon={TargetIcon} testid="kpi-cpa" accent={metrics.spend.data_available ? "rose" : "zinc"} />
        </div>
      )}

      {/* Attribution breakdown */}
      {metrics && metrics.attribution && (
        <div className="card-flat p-4 mb-6" data-testid="growth-attribution">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Attribution · signups by first-touch source</div>
            <div className="text-[11px] text-zinc-500">
              {metrics.attribution.attributed_signups} attributed · {metrics.attribution.organic_signups} organic/unknown
            </div>
          </div>
          {metrics.attribution.top_sources.length === 0 ? (
            <div className="text-[12px] text-zinc-500">
              No UTM-tagged signups yet. Share links like{" "}
              <code className="px-1 bg-zinc-100 rounded">https://skillgappilot.com/?utm_source=linkedin&amp;utm_campaign=jan-launch</code>{" "}
              to see which campaign drives paid subs.
            </div>
          ) : (
            <div className="space-y-2">
              {metrics.attribution.top_sources.map(s => {
                const max = Math.max(1, ...metrics.attribution.top_sources.map(x => x.signups));
                const pct = Math.max(4, Math.round((s.signups / max) * 100));
                return (
                  <div key={s.source} className="flex items-center gap-3" data-testid={`attr-row-${s.source}`}>
                    <div className="w-32 text-xs text-zinc-700 shrink-0 truncate">{s.source}</div>
                    <div className="flex-1 h-5 bg-zinc-100 rounded-md overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-emerald-500 to-sky-500" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="w-16 text-right text-xs font-mono font-semibold">{s.signups}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Funnel bars */}
      {funnel && (
        <div className="card-flat p-4 mb-6" data-testid="growth-funnel">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Funnel · last 30 days</div>
            <div className="text-[11px] text-zinc-500">verified items shown in colour · event-only items in grey</div>
          </div>
          <div className="space-y-2">
            {funnel.stages.map(s => (
              <div key={s.stage} className="flex items-center gap-3">
                <Bar label={`${s.stage}${s.drop_off_pct && s.drop_off_pct > 0 ? ` (-${s.drop_off_pct}%)` : ""}`}
                  value={s.value} max={maxStage} muted={!s.verified} />
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.verified ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>
                  {s.source}
                </span>
              </div>
            ))}
          </div>
          {funnel.stages.every(s => s.source !== "events" || s.value === 0) && (
            <div className="mt-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2">
              <AlertCircle size={12} className="shrink-0 mt-0.5"/>
              No visitor events logged yet. The tracker is live on landing + signup + pricing; drop-offs between those stages will appear once visitors arrive.
            </div>
          )}
        </div>
      )}

      {/* Agent tabs */}
      <div className="flex gap-2 mb-4" data-testid="growth-tabs">
        {AGENTS.map(a => (
          <button key={a.key} data-testid={`tab-${a.key}`}
            onClick={() => setTab(a.key)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold border transition
              ${tab === a.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"}`}>
            <a.icon size={14} /> {a.label}
          </button>
        ))}
      </div>

      {/* Agent run bar */}
      <div className="card-flat p-4 mb-4" data-testid={`agent-panel-${tab}`}>
        {tab === "conversion" && (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-sm font-display font-bold">Conversion Optimizer</div>
              <div className="text-xs text-zinc-500">Analyzes the real 30d funnel + Razorpay data and ranks the highest-impact fixes.</div>
            </div>
            <button onClick={runConversion} disabled={running} data-testid="run-conversion"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60">
              {running ? <Loader2 size={14} className="animate-spin" /> : <LineChart size={14} />} Analyze funnel
            </button>
          </div>
        )}
        {tab === "marketing" && (
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-sm font-display font-bold">Growth Marketing</div>
                <div className="text-xs text-zinc-500">Drafts channel-aware copy. Nothing publishes — review, approve, copy out.</div>
              </div>
              <button onClick={runMarketing} disabled={running} data-testid="run-marketing"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60">
                {running ? <Loader2 size={14} className="animate-spin" /> : <Megaphone size={14} />} Generate drafts
              </button>
            </div>
            <div className="grid md:grid-cols-4 gap-2">
              <input data-testid="mkt-audience" value={mkt.audience} onChange={e => setMkt({...mkt, audience: e.target.value})}
                placeholder="Audience" className="h-10 px-3 border border-zinc-300 rounded-md bg-white text-sm md:col-span-2" />
              <select data-testid="mkt-channel" value={mkt.channel} onChange={e => setMkt({...mkt, channel: e.target.value})}
                className="h-10 px-3 border border-zinc-300 rounded-md bg-white text-sm">
                <option value="any">Any channel</option>
                <option value="linkedin">LinkedIn</option>
                <option value="twitter">Twitter / X</option>
                <option value="reddit">Reddit</option>
                <option value="email">Email</option>
                <option value="reel">Reel / short video</option>
                <option value="ad">Paid ad</option>
              </select>
              <input data-testid="mkt-count" type="number" min={1} max={8} value={mkt.count}
                onChange={e => setMkt({...mkt, count: Number(e.target.value) || 4})}
                className="h-10 px-3 border border-zinc-300 rounded-md bg-white text-sm" />
            </div>
          </div>
        )}
        {tab === "leads" && (
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-sm font-display font-bold">High-Intent Lead Generation</div>
                <div className="text-xs text-zinc-500">
                  Find <b>real public Reddit posts</b> from people describing the exact pain SkillGap Pilot solves,
                  or discover communities to partner with. No scraping, no bulk DMs — drafts only.
                </div>
              </div>
              <div className="flex gap-1 shrink-0" data-testid="leads-mode-toggle">
                <button data-testid="leads-mode-reddit" onClick={() => toggleLeadsMode("reddit")}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${leadsMode === "reddit" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"}`}>
                  Real Reddit posts
                </button>
                <button data-testid="leads-mode-communities" onClick={() => toggleLeadsMode("communities")}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${leadsMode === "communities" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"}`}>
                  Discover communities
                </button>
              </div>
            </div>

            {leadsMode === "reddit" ? (
              <div className="space-y-2">
                {integrations?.reddit && integrations.reddit.configured === false && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2" data-testid="reddit-setup-banner">
                    <AlertCircle size={14} className="shrink-0 mt-0.5"/>
                    <div className="flex-1">
                      <div className="font-semibold">Reddit API credentials not configured.</div>
                      <div className="mt-1">
                        Create a free <b>web app</b> at{" "}
                        <a href={integrations.reddit.setup_url} target="_blank" rel="noreferrer" className="underline">reddit.com/prefs/apps</a>,
                        register the Redirect URI below exactly, then add{" "}
                        <code className="px-1 bg-amber-100 rounded">REDDIT_CLIENT_ID</code> and{" "}
                        <code className="px-1 bg-amber-100 rounded">REDDIT_CLIENT_SECRET</code>.
                      </div>
                      {integrations.reddit.redirect_uri && (
                        <div className="mt-1 flex items-center gap-2">
                          <span className="text-[10px] uppercase tracking-[0.15em] text-amber-700">Redirect URI</span>
                          <code className="text-[11px] bg-white border border-amber-200 rounded px-1.5 py-0.5 break-all">{integrations.reddit.redirect_uri}</code>
                          <CopyBtn text={integrations.reddit.redirect_uri} testid="copy-reddit-redirect"/>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {integrations?.reddit?.configured && (
                  <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 text-xs flex items-center gap-3 flex-wrap" data-testid="reddit-connect-strip">
                    {integrations.reddit.user_connected ? (
                      <>
                        <CheckCircle2 size={14} className="text-emerald-600 shrink-0"/>
                        <span>
                          Posting as <b>u/{integrations.reddit.reddit_username}</b>. Approved drafts show a “Post as me to Reddit” button.
                        </span>
                        <button onClick={disconnectReddit} data-testid="reddit-disconnect"
                          className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-100 text-[11px] font-semibold">
                          <Unlink size={12}/> Disconnect
                        </button>
                      </>
                    ) : (
                      <>
                        <AlertCircle size={14} className="text-amber-600 shrink-0"/>
                        <span>No Reddit account connected yet. Connect to enable the “Post as me” button on approved drafts.</span>
                        <button onClick={connectReddit} data-testid="reddit-connect"
                          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#FF4500] text-white hover:bg-[#e23e00] text-[11px] font-semibold">
                          <Link2 size={12}/> Connect Reddit
                        </button>
                      </>
                    )}
                  </div>
                )}
                <div className="grid md:grid-cols-6 gap-2">
                  <input data-testid="reddit-query" value={reddit.query}
                    onChange={e => setReddit({ ...reddit, query: e.target.value })}
                    placeholder='Query (e.g. "resume rejected OR interview failed")'
                    className="h-10 px-3 border border-zinc-300 rounded-md bg-white text-sm md:col-span-3" />
                  <input data-testid="reddit-subs" value={reddit.subreddits}
                    onChange={e => setReddit({ ...reddit, subreddits: e.target.value })}
                    placeholder="Subreddits, comma separated (optional)"
                    className="h-10 px-3 border border-zinc-300 rounded-md bg-white text-sm md:col-span-2" />
                  <select data-testid="reddit-time" value={reddit.time_filter}
                    onChange={e => setReddit({ ...reddit, time_filter: e.target.value })}
                    className="h-10 px-3 border border-zinc-300 rounded-md bg-white text-sm">
                    <option value="day">Past day</option>
                    <option value="week">Past week</option>
                    <option value="month">Past month</option>
                    <option value="year">Past year</option>
                    <option value="all">All time</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <input data-testid="reddit-limit" type="number" min={1} max={25} value={reddit.limit}
                    onChange={e => setReddit({ ...reddit, limit: Number(e.target.value) || 8 })}
                    className="h-10 px-3 border border-zinc-300 rounded-md bg-white text-sm w-28" placeholder="Count"/>
                  <select data-testid="reddit-tone" value={reddit.tone}
                    onChange={e => setReddit({ ...reddit, tone: e.target.value })}
                    className="h-10 px-3 border border-zinc-300 rounded-md bg-white text-sm">
                    <option value="helpful">Helpful</option>
                    <option value="direct">Direct</option>
                    <option value="curious">Curious</option>
                  </select>
                  <button onClick={runReddit} disabled={running || integrations?.reddit?.configured === false} data-testid="run-reddit"
                    title={integrations?.reddit?.configured === false ? "Add Reddit API credentials to enable" : ""}
                    className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed">
                    {running ? <Loader2 size={14} className="animate-spin" /> : <Compass size={14} />} Find real Reddit posts
                  </button>
                </div>
                <div className="text-[11px] text-zinc-500">
                  Reads Reddit's public search JSON (same data a logged-out visitor sees). Every result links to the real post so you can read context before replying manually.
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid md:grid-cols-4 gap-2">
                  <input data-testid="leads-niche" value={leads.niche} onChange={e => setLeads({...leads, niche: e.target.value})}
                    placeholder="Niche or audience" className="h-10 px-3 border border-zinc-300 rounded-md bg-white text-sm md:col-span-2" />
                  <select data-testid="leads-tone" value={leads.outreach_tone} onChange={e => setLeads({...leads, outreach_tone: e.target.value})}
                    className="h-10 px-3 border border-zinc-300 rounded-md bg-white text-sm">
                    <option value="helpful">Helpful</option>
                    <option value="curious">Curious</option>
                    <option value="direct">Direct</option>
                    <option value="partnership">Partnership</option>
                  </select>
                  <input data-testid="leads-count" type="number" min={1} max={10} value={leads.count}
                    onChange={e => setLeads({...leads, count: Number(e.target.value) || 5})}
                    className="h-10 px-3 border border-zinc-300 rounded-md bg-white text-sm" />
                </div>
                <div className="flex items-center justify-end">
                  <button onClick={runLeads} disabled={running} data-testid="run-leads"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60">
                    {running ? <Loader2 size={14} className="animate-spin" /> : <Compass size={14} />} Discover communities
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action list */}
      <div className="space-y-3" data-testid={`actions-list-${tab}`}>
        {agentActions.length === 0 ? (
          <div className="rounded-xl border border-dashed border-zinc-300 bg-white p-8 text-center text-sm text-zinc-500">
            No {AGENTS.find(a => a.key === tab)?.label} items yet. Click <b>Generate</b> above to get started.
          </div>
        ) : agentActions.map(a => (
          <ActionCard key={a.id} a={a} onDecision={decide}
            onPostToReddit={postToReddit}
            redditReady={!!integrations?.reddit?.user_connected} />
        ))}
      </div>
    </div>
  );
}
